import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { DatePipe } from '@angular/common';
import { Component, Input, ViewChild } from '@angular/core';
import {
  FormControl,
  FormGroupDirective,
  NgForm,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  of,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { BackendAccessService } from '../../../Services/APICalls/backend-access-service';
import { CanActivateService } from '../../../Services/CanActivate/can-activate.service';
import { LoadingService } from '../../../Services/Loading/loading-service.service';
import { BranchSelectionService } from '../../../Services/branch-selection.service';
import { ErrorMessageDialogComponent } from '../../../Shared Components/error-message-dialog/error-message-dialog.component';
import { DDIGCompanySearchOnly } from '../../../interfaces/DDIGCompanySearchOnly.interface';
import {
  ImplementationSite,
  MobileCredsLandingPageTable,
} from '../../../interfaces/MobileCredsLandingPageTable.interface';
import { UserProfileData } from '../../../interfaces/user-profile-data';
import { CMCPDetailsDialogComponent } from '../CMCP-details-dialog/CMCP-details-dialog.component';
import { CPDetailsDialogComponent } from '../CP-details-dialog/cp-details-dialog.component';
import { AccountsServiceService } from '../../../Services/accounts-service.service';

/* istanbul ignore next */
export class NoResultsErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    return !!(control && control.invalid && (control.dirty || control.touched));
  }
}

@Component({
  selector: 'app-mobile-cred-landing',
  templateUrl: './mobile-cred-landing.component.html',
  styleUrls: ['./mobile-cred-landing.component.scss'],
  animations: [
    trigger('slideDownInOut', [
      state(
        'void',
        style({
          opacity: 0,
          transform: 'translateY(-10%)',
        })
      ),
      transition('void => *', animate('600ms ease-out')),
      transition('* => void', animate('600ms ease-in')),
    ]),
    trigger('slideDownAnimation', [
      state('void', style({ transform: 'translateY(-100%)', opacity: 0 })),
      state('*', style({ transform: 'translateY(100)', opacity: 1 })),
      transition(':enter', [animate('0.5s ease-out')]),
      transition(':leave', [animate('0.5s ease-in')]),
    ]),
  ],
  standalone: false,
})
export class MobileCredLandingComponent {
  companyName$: Observable<string | undefined>;
  displayedColumns: string[] = [
    'implementationName',
    'totalBillableLicenses',
    'solutionType',
    'vertical',
    'moreDetails',
  ];
  private destroy$ = new Subject<void>();
  dataSource = new MatTableDataSource<ImplementationSite>();
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  selectedBranchId: string | null = null;
  private companyNameSubject = new BehaviorSubject<string | undefined>(
    undefined
  );
  companyId: string | null = null;
  noResultsFound: boolean = false;
  form2!: UntypedFormGroup;
  form1!: UntypedFormGroup;
  form3!: UntypedFormGroup;
  filteredOptions: Observable<DDIGCompanySearchOnly[]> | undefined;
  options: DDIGCompanySearchOnly[] = [];
  errorStateMatcher: ErrorStateMatcher = new NoResultsErrorStateMatcher();
  companyName: string = '';
  // companyName: string | undefined;
  showLicenseTracker = false;
  // licenseTrackerData: MobileCredsLandingPageTable | null = null;
  licenseTrackerData: MobileCredsLandingPageTable = {
    totalCpLicenses: 0,
    totalCpCmLicenses: 0,
    implementationSites: [],
  };
  userProfileData: UserProfileData | undefined;
  private companyProfileSubject$ = new BehaviorSubject<any>(null);
  companyProfile$ = this.companyProfileSubject$.asObservable();
  selectedBranchSubscription: Subscription | undefined;
  singleBranchInfo: any;
  @Input() branches: any[] = [];
  @Input() companyProfile: any;
  selectedBranch!: string | null;
  selectedAccountName: string | undefined;
  selectedAccountNumber: string | undefined;

  constructor(
    public formBuilder: UntypedFormBuilder,
    public datePipe: DatePipe,
    public dialog: MatDialog,
    private loadingService: LoadingService,
    public canActivateService: CanActivateService,
    public branchSelectionService: BranchSelectionService,
    public backend: BackendAccessService,
    public accountsService: AccountsServiceService
  ) {
    this.companyName$ = this.canActivateService.companyProfile$.pipe(
      map((companyProfile) => companyProfile?.companyName)
    );
    this.companyName$ = this.companyNameSubject.asObservable();
  }
  /* istanbul ignore next */
  ngOnInit(): void {
    console.log('MobileCredLandingComponent ngOnInit');
    localStorage.removeItem('CPDetailsData');
    localStorage.removeItem('CPCMDetailsData');

    combineLatest([
      this.canActivateService.companyProfile$.pipe(distinctUntilChanged()),
      this.canActivateService.multipleBranches$.pipe(distinctUntilChanged()),
      this.branchSelectionService.selectedBranch$.pipe(distinctUntilChanged()),
    ])
      .pipe(
        takeUntil(this.destroy$),
        tap(([companyProfile, hasMultipleBranches, selectedBranchId]) => {
          if (companyProfile) {
            this.companyId = companyProfile.companyAccountId;
            this.branchSelectionService.setCompanyName(
              companyProfile.companyName
            );
          }
          if (!hasMultipleBranches) {
            this.selectedBranchId = this.companyId!;
            this.branchSelectionService.setSelectedBranch(this.companyId!);
          } else {
            this.selectedBranchId = selectedBranchId;
          }
        }),
        filter(
          ([_, hasMultipleBranches, selectedBranchId]) =>
            !hasMultipleBranches || !!selectedBranchId
        ),
        switchMap(([_, hasMultipleBranches, selectedBranchId]) => {
          const branchId = hasMultipleBranches
            ? selectedBranchId
            : this.companyId;
          return this.loadMobileCredSummaryData(branchId);
        }),
        catchError((error) => {
          console.error('Error during data loading:', error);
          return of([]);
        }),
        finalize(() => this.loadingService.hide())
      )
      .subscribe();

    this.form3 = this.formBuilder.group({
      myControl: new UntypedFormControl('', [Validators.required]),
    });

    this.filteredOptions = this.form3.controls['myControl'].valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      startWith(''),
      map((value) => (typeof value === 'string' ? value : value.businessId)),
      switchMap((businessId) =>
        businessId
          ? this.loadCompaniesTechSupport(businessId)
          : of(this.options.slice())
      )
    );
    this.loadingService.hide();
  }

  /* istanbul ignore next */
  private processData(data: any): void {
    this.licenseTrackerData = data;
    this.dataSource.data = data.implementationSites;
    if (data.implementationSites.length === 0) {
      this.openNoBranchConfiguredDialog();
    }
  }

  /* istanbul ignore next */
  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  /* istanbul ignore next */
  loadMobileCredSummaryData(selectedBranch?: string | null): Observable<any> {
    this.loadingService.show();
    return this.backend.getMobileCredsOverviewData(selectedBranch!).pipe(
      tap((data) => {
        this.processData(data);
      }),
      catchError((error) => {
        console.error('Error fetching overview data:', error);
        return of([]);
      }),
      finalize(() => this.loadingService.hide())
    );
  }

  /* istanbul ignore next */
  loadCompaniesTechSupport(value: string): Observable<DDIGCompanySearchOnly[]> {
    if (!value) {
      return of([]); // Don't call the API if the input is empty
    }
    this.loadingService.show();
    return this.backend.searchAccountsMobileCreds(value).pipe(
      tap((results: DDIGCompanySearchOnly[]) => {
        this.options = results;
        this.accountsService.DDIGaccounts.next(results);
        this.noResultsFound = results.length === 0;
        this.form3.controls['myControl'].setErrors(
          this.noResultsFound ? { noResults: true } : null
        );
      }),
      catchError((error) => {
        this.loadingService.hide();
        return of([]); // Return an empty observable in case of an error
      }),
      finalize(() => this.loadingService.hide())
    );
  }

  /* istanbul ignore next */
  loadMobileCredSummaryTechSupport(event: MatAutocompleteSelectedEvent): void {
    // Extract the branchId (assuming accountId is used as branchId) from the selected option
    const branchId = event.option.value.accountId;
    this.companyNameSubject.next(event.option.value.businessId);
    this.selectedBranchId = branchId;
    this.selectedAccountName = event.option.value.businessId;
    this.selectedAccountNumber = event.option.value.accountNumber;

    // Now call the backend service with the branchId
    this.backend
      .getMobileCredsOverviewData(branchId)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          this.dataSource.data = data.implementationSites;
          this.licenseTrackerData = data;
          if (data.implementationSites.length === 0) {
            this.openNoBranchConfiguredDialog();
          }
        },
        error: (error) => {
          console.error('Failed to load API Credential Overview Data:', error);
        },
      });
  }

  /* istanbul ignore next */
  displayNameMobileCreds(subject: any): string {
    if (!subject) {
      return '';
    }
    if (subject.businessId) {
      return (
        subject.businessId +
        ' (Account #: ' +
        (subject.accountNumber ? subject.accountNumber : 'N/A') +
        ')' +
        '\n' +
        ' ' +
        subject.addressCity +
        ', ' +
        subject.addressState
      );
    } else if (subject.accountName) {
      return subject.accountName;
    } else {
      return '';
    }
  }

  /* istanbul ignore next */
  openNoBranchConfiguredDialog(): void {
    const dialogRef = this.dialog.open(ErrorMessageDialogComponent, {
      width: '400px',
    });
    dialogRef.afterClosed().subscribe((result) => {
      console.log('The dialog was closed');
    });
  }

  /* istanbul ignore next */
  // viewLicenseTracker(): void {
  //   const dialogRef = this.dialog.open(LicenseTrackerDialogComponent, {
  //     width: '500px',
  //     data: {
  //       totalCpCmLicenses: this.licenseTrackerData?.totalCpCmLicenses,
  //       totalCpLicenses: this.licenseTrackerData?.totalCpLicenses,
  //       companyName: this.companyName,
  //     },
  //   });
  // }
  /* istanbul ignore next */
  toggleLicenseTracker(): void {
    this.showLicenseTracker = !this.showLicenseTracker;
  }
  /* istanbul ignore next */
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
  /* istanbul ignore next */
  openDetailsDialog(element: any): void {
    this.loadingService.show();
    const dialogComponent: any =
      element.solutionType === 'CMCP'
        ? CMCPDetailsDialogComponent
        : CPDetailsDialogComponent;
    const dialogData = {
      brid: this.selectedBranchId,
      clid: element.clientId,
      mpt: element.mappingType,
      implementationName: element.implementationName,
    };
    this.loadingService.hide();
    this.dialog.open(dialogComponent, {
      // width: '80%',
      width: '99%',
      data: dialogData,
    });
    this.loadingService.hide();
  }
  /* istanbul ignore next */
  onBranchChanged(newBranch: string) {
    this.selectedBranchId = newBranch;
    this.branchSelectionService.setSelectedBranch(newBranch);
    this.branchSelectionService.branchSelected.emit({
      selectedBranch: newBranch,
      branches: this.branches,
    });
  }
}
